import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import {isNotNullOrUndefined, SetState} from '@wandb/weave/common/util/types';
import produce from 'immer';
import _ from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {useFeaturedReportsMetadataQuery} from '../../generated/graphql';
import {ReorderFn} from '../../util/dragDrop';
import {
  ShowcasedReport,
  useEntityPageContext,
  useEntityPageUpdaterContext,
} from './context';
import {ReportShowcaseCards} from './ReportShowcaseCards';
import {getShowcasedReport} from './util';

type ReportShowcaseSectionProps = {};

export const ReportShowcaseSection: React.FC<ReportShowcaseSectionProps> =
  React.memo(() => {
    const {showcasedReportIDs, readOnly} = useEntityPageContext();
    const {setShowcasedReportIDs} = useEntityPageUpdaterContext();

    const sortedReportIDs = useSortedIDs(showcasedReportIDs);
    const {loading, data} = useFeaturedReportsMetadataQuery({
      variables: {ids: sortedReportIDs},
    });

    const showcasedReports: ShowcasedReport[] = useMemo(() => {
      const metadataNodes = data?.views?.edges.map(e => e.node) ?? [];
      const metadataByReportID: Map<string, (typeof metadataNodes)[number]> =
        new Map(
          metadataNodes
            .map(n => {
              if (n == null) {
                return null;
              }
              return [n.id, n] as const;
            })
            .filter(isNotNullOrUndefined)
        );

      return showcasedReportIDs
        .map(id => {
          const metadata = metadataByReportID.get(id);
          if (metadata == null) {
            return null;
          }
          return getShowcasedReport(metadata);
        })
        .filter(isNotNullOrUndefined);
    }, [showcasedReportIDs, data]);

    const reorderShowcasedReport = useReorderShowcasedReport({
      ids: showcasedReportIDs,
      reports: showcasedReports,
      setIDs: setShowcasedReportIDs,
    });

    if (loading) {
      return <WandbLoader name="report-showcase-section-loader" />;
    }

    return (
      <ReportShowcaseCards
        items={showcasedReports}
        reorderItems={reorderShowcasedReport}
        readOnly={readOnly}
      />
    );
  });

// HAX: We don't want the query to refetch when the order of the IDs changes.
// This function only returns a new object when the set of IDs changes.
function useSortedIDs(ids: string[]): string[] {
  const sortedIDString = useMemo(() => _.sortBy(ids).join(','), [ids]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => _.sortBy(ids), [sortedIDString]);
}

type UseReorderShowcasedReportParams = {
  ids: string[];
  reports: ShowcasedReport[];
  setIDs: SetState<string[]>;
};

// HAX: The list if reports may differ from the list of IDs because
// we don't show reports for which metadata fetching fails.
// This can happen if the user showcases a report that is subsequently deleted.
// To account for this possibility, we must map report indices to ID indices
// so the reordering works properly.
// TODO(axel): This is NASTY. You can do better.
function useReorderShowcasedReport({
  ids,
  reports,
  setIDs,
}: UseReorderShowcasedReportParams): ReorderFn {
  const idIndexByReportIndex: Map<number, number> = useMemo(() => {
    const reportIndexByID: Map<string, number> = new Map(
      reports.map((r, i) => [r.id, i])
    );
    const idIndexByID: Map<string, number> = new Map(
      ids.map((id, i) => [id, i])
    );

    return new Map(
      reports
        .map(r => {
          const reportIndex = reportIndexByID.get(r.id);
          const idIndex = idIndexByID.get(r.id);
          if (reportIndex == null || idIndex == null) {
            return null;
          }
          return [reportIndex, idIndex] as const;
        })
        .filter(isNotNullOrUndefined)
    );
  }, [ids, reports]);

  return useCallback(
    (fromReportIndex: number, toReportIndex: number) => {
      const fromIDIndex = idIndexByReportIndex.get(fromReportIndex);
      const toIDIndex = idIndexByReportIndex.get(toReportIndex);
      if (fromIDIndex == null || toIDIndex == null) {
        return;
      }
      setIDs(prev =>
        produce(prev, draft => {
          const item = draft[fromIDIndex];
          draft.splice(fromIDIndex, 1);
          draft.splice(toIDIndex, 0, item);
        })
      );
    },
    [idIndexByReportIndex, setIDs]
  );
}
